'use client';

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Link from 'next/link';
import Image from 'next/image';
import { MdOutlineKeyboardArrowRight } from 'react-icons/md';
import colors from '../../styles/colors';

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel"

export default function BestSeller() {
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [products, setProducts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(
          'https://solar-backend-5nh9.onrender.com/public/categories'
        );
        const categoryData = res.data.items || [];
        setCategories(categoryData);
        if (categoryData.length > 0) {
          setActiveCategory(categoryData[0]._id);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      if (!activeCategory) return;
      setLoading(true);
      try {
        const res = await axios.get(
          `https://solar-backend-5nh9.onrender.com/public/categories/${activeCategory}/products`
        );
        const productList = res.data.items || [];
        setProducts(productList);
      } catch (err) {
        console.error('Error fetching products:', err);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [activeCategory]);

  return (
    <div
      className="w-full overflow-hidden mt-12 xl:px-8 md:px-5 px-3 xl:py-8 2xl:py-8 md:py-6 py-10 flex flex-col items-start"
      style={{ backgroundColor: colors.background }}
    >

      {/* Heading */}
      <h2 className="text-[#060606] font-bold text-xl pl-2">Bestsellers</h2>

      {/* Category Tabs */}
      <div
        className="max-w-[85.3rem] px-2 
             flex xl:justify-center 2xl:justify-center justify-start 
             xl:gap-[4rem] 2xl:gap-[4rem] md:gap-8 gap-5
             items-center
             overflow-x-auto xl:overflow-x-hidden 2xl:overflow-x-hidden
             hide-scrollbar"
      >
        {categories.map((cat) => (
          <button
            key={cat._id}
            onClick={() => setActiveCategory(cat._id)}
            className={`flex-shrink-0 min-w-fit text-[#5e5757] font-semibold cursor-pointer py-2 transition-all duration-500 ease-in-out hover:text-[#f73b02] hover:border-b-[#e92b0a] ${activeCategory === cat._id
              ? 'text-[#f84807] border-b-2 border-[#f84807]'
              : ''
              }`}
          >
            {cat.name}
          </button>
        ))}
      </div>


      {/* Products Carousel */}
      <div className="w-full pt-6 overflow-hidden relative">
        {loading || products === null ? (
          <div className="flex flex-col gap-2.5 justify-center items-center text-center h-auto">
            <svg className="w-8 h-8 text-[#0956f0] animate-spin" viewBox="0 0 24 24" fill="currentColor"><rect x="11" y="1" width="2" height="5" opacity="1" /><rect x="11" y="1" width="2" height="5" transform="rotate(30 12 12)" opacity="0.9" /><rect x="11" y="1" width="2" height="5" transform="rotate(60 12 12)" opacity="0.8" /><rect x="11" y="1" width="2" height="5" transform="rotate(90 12 12)" opacity="0.7" /><rect x="11" y="1" width="2" height="5" transform="rotate(120 12 12)" opacity="0.6" /><rect x="11" y="1" width="2" height="5" transform="rotate(150 12 12)" opacity="0.5" /><rect x="11" y="1" width="2" height="5" transform="rotate(180 12 12)" opacity="0.4" /><rect x="11" y="1" width="2" height="5" transform="rotate(210 12 12)" opacity="0.3" /><rect x="11" y="1" width="2" height="5" transform="rotate(240 12 12)" opacity="0.2" /><rect x="11" y="1" width="2" height="5" transform="rotate(270 12 12)" opacity="0.1" /><rect x="11" y="1" width="2" height="5" transform="rotate(300 12 12)" opacity="0.05" /><rect x="11" y="1" width="2" height="5" transform="rotate(330 12 12)" opacity="0.02" /></svg>
            <h1 className="text-[#0655e7]">Loading Products...</h1>
          </div>
        ) : !loading && products.length === 0 ? (
          <p className="text-center text-gray-500 py-10">
            No products available in this category.
          </p>
        ) : (
          <Carousel
            opts={{ align: "start" }}
            className="w-full mx-auto"
          >
            <CarouselContent>
              {products.map((product, idx) => (
                <CarouselItem key={product._id || idx} className="basis-[60%]  sm:basis-[45%] md:basis-[30%] lg:basis-[23%] xl:basis-[18%] 2xl:basis-[15%]">
                  <Link
                    key={product._id || idx}
                    href={`/SingleProduct/${product._id}`}
                  >
                    <div className="border overflow-hidden rounded max-w-[212px] min-h-[337px] sm:w-[14rem] border-[#D8D8D8] bg-[#faf9f9]  flex flex-col items-center hover:shadow-md transition">
                      {/* Product Image */}
                      <div className="w-full h-[12rem] relative flex justify-center items-center p-3">
                        {product.coverImage?.url ? (
                          <Image
                            src={product.coverImage.url}
                            alt={
                              product.coverImage.alt ||
                              product.title ||
                              'Product'
                            }
                            fill
                            className="object-scale-down"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400 text-sm">
                            No Image
                          </div>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="w-full flex flex-col justify-start items-start p-2">
                        <h3 className="text-sm text-gray-700 line-clamp-2 mb-1">
                          {product.title}
                        </h3>
                        <p className="font-semibold text-black">
                          {product.spec?.brand}
                        </p>
                        <button className="mt-3 cursor-pointer w-full border border-[#2F73C2] text-[#353434] outline-none px-4 py-1 rounded-lg hover:bg-[#042844] hover:text-white transition">
                          Get Quote
                        </button>
                      </div>

                      {/* Quote Button */}

                    </div>
                  </Link>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        )}
      </div>

      {!loading && products && products.length > 0 && (
        <div className="w-full flex justify-center items-center mt-6">
          <Link
            href="/products/displayProducts"
            className="text-blue-600 flex gap-1.5 items-center"
          >
            View All{' '}
            <MdOutlineKeyboardArrowRight className="text-gray-400" size={20} />
          </Link>
        </div>
      )}
    </div>
  );
}
